import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AssessmentStatus,
  StudentOverTimeBenchmarkResult,
  StudentOverTimeReport
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { ProductType } from '@shared/enums/product-type';
import { ProgressReportType } from '@shared/enums/progress-report-type';
import { RoleType } from '@shared/enums/role-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { ScoreDetailModalComponent } from '@shared/modals/score-detail-modal/score-detail-modal.component';
import { catchAndRedirectNonErrorToMainFlow } from '@shared/zb-rxjs-operators/rxjs-operators';
import { BehaviorSubject, from, mergeMap, Subscription } from 'rxjs';
import { BrandedReportsHelpers } from '../branded-reports-helpers';
import { BarColors, BorderColors, BorderStyles, TextColors } from '../zbchart-report/bar-colors.enum';
import { ZbChartReportTableComponent } from '../zbchart-report/zbchart-report-table.component';
import { ZbChartBar, ZbChartBarValue, ZbChartData } from '../zbchart-report/zbchart-report.model';

@Component({
  selector: 'zbp-branded-individual-student-over-time-report-detail',
  imports: [
    ZbChartReportTableComponent,
    TranslateModule,
  ],
  templateUrl: './branded-individual-student-over-time-report-detail.component.html',
  styleUrl: './branded-individual-student-over-time-report-detail.component.scss'
})
export class BrandedIndividualStudentOverTimeReportDetailComponent implements OnChanges, OnInit {

  @Input() showProgressReportsHelp: boolean = false;
  @Input({ required: true }) role: RoleType;
  @Input({ required: true }) productType: ProductType;
  @Input({ required: true }) productName: string;
  @Input({ required: true }) variant: string;
  @Input({ required: true }) classroomId: string;
  @Input({ required: true }) className: string;
  @Input({ required: true }) studentId: string;
  @Input({ required: true }) studentOverTimeReport?: StudentOverTimeReport;
  @Output() refreshData = new EventEmitter();
  @Input() isPrinting: boolean;
  @Input() printableReportHeadings: IPrintableReportHeading[];

  zbChart?: ZbChartData;

  private reports$ = new BehaviorSubject<StudentOverTimeReport>(null);
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private ngbModal: NgbModal,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
  ) {
  }

  ngOnInit() {
    this.subscriptions.add(
      this.reports$.subscribe((report) => {
        this.createZbChart(report);
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.studentOverTimeReport) {
      this.reports$.next(changes.studentOverTimeReport.currentValue);
    }

    // When this input changes to true, the print function is executed.
    // The caller is responsible for resetting the page after print is complete through window.onafterprint.
    if (changes.isPrinting && this.isPrinting) {
      this.print();
    }
  }

  createZbChart(
    report: StudentOverTimeReport,
  ): ZbChartData {
    if (!report) {
      return null;
    }

    const chartData = { bars: [] } as ZbChartData;
    chartData.showZeroCount = true;

    [...report.benchmarkResults]
      .forEach((result: StudentOverTimeBenchmarkResult) => {
        let ariaLabel = 'Bar, ';
        const row = {} as ZbChartBar;
        const values = [] as ZbChartBarValue[];

        const items = this.benchmarkToBarDataItemOption(result);

        if (items && items.length === 2) {
          // Completed: items[0] = colored bar (score percentage only)
          const scoreItem = items[0];
          const scorePercentage = scoreItem.value as number;

          // Add only the score bar (partial width based on percentage)
          values.push({
            barLabel: scoreItem.name,
            color: scoreItem.itemStyle.color as BarColors,
            borderColor: scoreItem.itemStyle.borderColor as BorderColors,
            percent: scorePercentage,
            count: result.totalCorrect,
            tooltipLabel: '',
            tooltipValue: scorePercentage,
          });

          // Set totalCount so bar width is calculated correctly
          row.totalCount = result.totalQuestions;
          row.fullLengthButton = false;
          row.labelOnLeft = true;
          ariaLabel += `${scoreItem.name}`;
        } else if (items && items.length === 1) {
          // NotStarted, AddScore, InProgress, PartiallyScored: only one item (full width bar)
          const item = items[0];

          values.push({
            barLabel: item.name,
            barLabelTranslationKey: item.nameTranslationKey,
            color: item.itemStyle.color as BarColors,
            borderColor: item.itemStyle.borderColor as BorderColors,
            percent: item.value as number,
            count: item.value as number,
            borderStyle: item.itemStyle.borderType === 'dashed' ? BorderStyles.dashed : BorderStyles.solid,
            barTextColor: (item.label?.color as TextColors) || TextColors.black,
            tooltipLabel: '',
            tooltipValue: item.name,
          });

          ariaLabel += `${item.name}`;
          // Make bar span full width
          row.fullLengthButton = true;
        }

        row.label = result.assessmentName;
        row.values = values;
        row.ariaLabel = ariaLabel;
        row.tooltip = this.sanitizer.bypassSecurityTrustHtml(BrandedReportsHelpers.zbChartTooltipFormatter(row));
        // if role type is parent and there's no score, don't add onClick
        if (this.role === RoleType.Teacher
          || (this.role === RoleType.Parent && result.assessmentStatus === AssessmentStatus.Completed)) {
          row.onClick = () => this.onChartClick(result);
        }
        chartData.bars.push(row);
      });

    this.zbChart = chartData;
    return chartData;
  }

  private benchmarkToBarDataItemOption(
    result: StudentOverTimeBenchmarkResult,
  ): (any & { meta: StudentOverTimeBenchmarkResult })[] {
    if (result.assessmentStatus === AssessmentStatus.Completed) {
      // Determine color based on benchmark
      let barColor = BarColors.red300;
      let borderColor = BorderColors.borderForRed300;

      if (result.benchmark === ScoreBenchmark.Meets) {
        barColor = BarColors.cream300;
        borderColor = BorderColors.borderForCream300;
      } else if (result.benchmark === ScoreBenchmark.Exceeds) {
        barColor = BarColors.green300;
        borderColor = BorderColors.borderForGreen300;
      }

      return [{
        meta: result,
        name: result.score,
        value: result.scorePercentage,
        itemStyle: {
          color: barColor,
          borderColor,
        },
      },
      {
        meta: result,
        name: result.score,
        value: 100 - result.scorePercentage,
      }];
    }
    if (result.assessmentStatus === AssessmentStatus.NotStarted) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-not-started',
        value: 100.00,
        itemStyle: {
          color: BarColors.white,
          borderColor: BorderColors.borderForWhite,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
        },
      }];
    }
    if (result.assessmentStatus === AssessmentStatus.AddScore) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-add-score',
        value: 100.00,
        itemStyle: {
          color: BarColors.white,
          borderType: 'dashed',
          borderColor: BorderColors.borderForWhite,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
          color: TextColors.brandPrimary,
        },
      }];
    }
    if (result.assessmentStatus === AssessmentStatus.ReadyForScoring) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-ready-for-scoring',
        value: 100.00,
        itemStyle: {
          color: BarColors.white,
          borderType: 'dashed',
          borderColor: BorderColors.borderForWhite,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
          color: TextColors.brandPrimary,
        },
      }];
    }
    if (result.assessmentStatus === AssessmentStatus.PartiallyScored) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-partially-scored',
        value: 100.00,
        itemStyle: {
          color: BarColors.white,
          borderType: 'dashed',
          borderColor: BorderColors.borderForWhite,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
          color: TextColors.brandPrimary,
        },
      }];
    }
    if (result.assessmentStatus === AssessmentStatus.InProgress) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-in-progress',
        value: 100.00,
        itemStyle: {
          color: BarColors.neutralsWarmGrey200,
          borderColor: BorderColors.borderForNeutralsWarmGrey200,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
        },
      }];
    }
    // If parent role, scoring in progress and score not available are possible statuses
    if (result.assessmentStatus === AssessmentStatus.ScoringInProgress) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-scoring-in-progress',
        value: 100.00,
        itemStyle: {
          color: BarColors.neutralsWarmGrey200,
          borderColor: BorderColors.borderForNeutralsWarmGrey200,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
        },
      }];
    }
    // If parent role, scoring in progress and score not available are possible statuses
    if (result.assessmentStatus === AssessmentStatus.ScoreNotAvailable) {
      return [{
        meta: result,
        nameTranslationKey: 'shared-assessment-status-label-score-not-available',
        value: 100.00,
        itemStyle: {
          color: BarColors.neutralsWarmGrey200,
          borderColor: BorderColors.borderForNeutralsWarmGrey200,
        },
        label: {
          show: true,
          position: 'inside',
          align: 'center',
        },
      }];
    }
    return null;
  }

  onChartClick(benchmarkResult: StudentOverTimeBenchmarkResult) {
    // get metadata that we sneak onto the model during chart generation
    const result: StudentOverTimeBenchmarkResult = benchmarkResult;
    if (!result) {
      // ignore chart clicks that do not target a row
      return;
    }

    const modalRefObservable = from(ScoreDetailModalComponent.openAssessment(this.ngbModal, {
      activityName: result.assessmentName,
      className: this.className,
      productName: this.productName,
      score: result.score,
      studentName: `${this.reports$.value?.firstName} ${this.reports$.value?.lastName}`,
      topicType: TopicType.Assessment,
      parts: result.scoreDetail as any, // NOT a typo
      benchmark: result.benchmark,
      productType: this.productType,
      variant: this.variant,
      classroomId: this.classroomId,
      studentId: this.studentId,
      assessmentKey: result.assessmentKey,
      scoreIsOverridden: result.scoreIsOverridden,
      assessmentStatus: result.assessmentStatus,
      reportName: ProgressReportType.IndividualStudentOverTime,
      allowEdit: this.role === RoleType.Teacher,
      completedOn: result.completedOn,
      lastOverriddenUserName: result.lastOverriddenUserName,
      lastOverriddenOn: result.lastOverriddenOn,
      wordListLevel: ScoreDetailModalComponent.translateWordListLevel(result.wordListLevel, this.translateService),
    })).pipe(
      mergeMap(ref => ref.result),
      catchAndRedirectNonErrorToMainFlow(),
    );

    this.subscriptions.add(modalRefObservable.subscribe((res) => {
      if (res?.refreshData) {
        this.refreshData.emit();
      }
    }));
  }

  /**  Prints the report. */
  print() {
    // Give time for Printable Header to render
    setTimeout(() => {
      window.print();
    }, 0);
  }
}
